import { localize } from "../localization/localize";
import { IHttpService, IIntervalService, IPromise, IRootScopeService } from "angular";
import { EnvironmentService } from "@gtmhub/env";
import { IStatusData, StatusBarService } from "@gtmhub/shared/components";
import { ISelectedCloningSession } from "@gtmhub/sessions";
import { IUIError, UIErrorHandlingService } from "@gtmhub/error-handling";
import { IMetricCloneSelected, OkrEvents } from "@gtmhub/okrs";
import { GoalService } from "@gtmhub/goals/goalService";
import { IMetric, MetricService } from "@gtmhub/okrs/metrics";
import { IGoalCloneSelected } from "@gtmhub/goals/controllers/clone-or-move-goal";
import { IGoal } from "@gtmhub/goals/models";

export enum CloneJobStatus {
  Pending = "pending",
  InProgress = "in_progress",
  Done = "done",
  ToBeReverted = "to_be_reverted",
  Reverting = "reverting",
  Reverted = "reverted",
}

export enum ResourceType {
  Session = "session",
  KeyResult = "kr",
  Objective = "okr",
}

export type ClonedIds = Record<string, string>;

export interface CloneResourceRequest {
  type: ResourceType;
  resourceId: string;
  targetType: ResourceType;
  targetResourceId: string;
  resetProgress?: boolean;
  cloneChildren?: boolean;
  cloneTasks?: boolean;
  cloneTasksStatus?: boolean;
  sendNotifications?: boolean;
  keepUpdatesInSync?: boolean;
}

export interface ICloneJobId {
  id: string;
}

export interface ICloneJob extends ICloneJobId {
  status: CloneJobStatus;
  clonedIds: ClonedIds;
  rootClonedId: string;
  resourceType: ResourceType;
}

export interface CloneRequest {
  source: string;
  target: ISelectedCloningSession;
}

export class CloneResourceService {
  public static $inject = ["$http", "$rootScope", "$interval", "EnvironmentService", "StatusBarService", "UIErrorHandlingService", "MetricService", "GoalService"];

  private okrEvents: OkrEvents;

  constructor(
    private $http: IHttpService,
    private $rootScope: IRootScopeService,
    private $interval: IIntervalService,
    private env: EnvironmentService,
    // TODO we should get rid of statusBarService and use snackBar instead later on
    private statusBarService: StatusBarService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private metricService: MetricService,
    private goalService: GoalService
  ) {
    this.okrEvents = new OkrEvents($rootScope);
  }

  public cloneSession(cloneRequest: CloneRequest): IPromise<void> {
    const payload: CloneResourceRequest = {
      type: ResourceType.Session,
      resourceId: cloneRequest.source,
      targetType: ResourceType.Session,
      targetResourceId: cloneRequest.target.sessionId,
      cloneChildren: !cloneRequest.target.cloneOnlyParentObjectives,
      cloneTasks: cloneRequest.target.cloneTasks,
      cloneTasksStatus: true,
      resetProgress: cloneRequest.target.resetProgress,
      // for session cloning the message is "Do not send notifications" when ticked
      sendNotifications: !cloneRequest.target.sendNotifications,
    };
    return this.cloneInternal(payload.type, payload, cloneRequest.target);
  }
  public cloneGoal(source: IGoal, cloneOptions: IGoalCloneSelected): IPromise<void> {
    const targetType: ResourceType = cloneOptions.goal ? ResourceType.Objective : ResourceType.Session;
    const targetResourceId = cloneOptions.goal ? cloneOptions.goal.id : cloneOptions.session.id;

    const payload: CloneResourceRequest = {
      type: ResourceType.Objective,
      resourceId: source.id,
      targetType: targetType,
      targetResourceId: targetResourceId,
      cloneChildren: cloneOptions.cloneChildren,
      cloneTasks: cloneOptions.cloneTasks,
      cloneTasksStatus: true,
      resetProgress: !cloneOptions.progress,
      sendNotifications: cloneOptions.notifications,
    };

    return this.cloneInternal(ResourceType.Objective, payload, cloneOptions, source);
  }
  public cloneMetric(source: IMetric, cloneOptions: IMetricCloneSelected): IPromise<void> {
    const payload: CloneResourceRequest = {
      type: ResourceType.KeyResult,
      resourceId: source.id,
      targetType: ResourceType.Objective,
      targetResourceId: cloneOptions.goal.id,
      resetProgress: !cloneOptions.progress,
      sendNotifications: cloneOptions.notifications,
      keepUpdatesInSync: cloneOptions.sync,
      cloneTasks: true,
      cloneTasksStatus: true,
    };

    return this.cloneInternal(ResourceType.KeyResult, payload, cloneOptions);
  }

  public cloneInternal(
    type: ResourceType,
    payload: CloneResourceRequest,
    cloneOptions?: IMetricCloneSelected | IGoalCloneSelected | ISelectedCloningSession,
    source?: IGoal | IMetric
  ): IPromise<void> {
    // show status bar
    this.statusBarService.show();

    // create job item
    return this.scheduleCloneJob(payload)
      .then((cloneJobId: ICloneJobId) => {
        /** Ping the clone api every 3 seconds until the status is done or reverted. */
        const updateJobStatusInterval = this.$interval(() => {
          if (this.statusBarService.isVisible()) {
            // update status is async op due to dom element being creating and attached to the tree
            this.statusBarService.updateStatus({ status: "in_progress", action: "clone" });

            this.$interval.cancel(updateJobStatusInterval);
            const retryJobStatusInterval = this.$interval(() => {
              this.fetchCloneJobById(cloneJobId)
                .then((cloneJob) => {
                  const done = CloneJobStatus.Done === cloneJob.status;
                  const reverted =
                    CloneJobStatus.Reverted == cloneJob.status || CloneJobStatus.Reverting == cloneJob.status || CloneJobStatus.ToBeReverted == cloneJob.status;
                  if (done) {
                    this.$interval.cancel(retryJobStatusInterval);
                    this.updateProgressBar(type, cloneJob, cloneOptions, source);
                  } else if (reverted) {
                    this.$interval.cancel(retryJobStatusInterval);
                    this.handleCloningError({
                      type: "unexpected",
                      title: localize("clone_job_reverted_title"),
                      message: localize("clone_job_reverted_message"),
                      showIntercomLink: true,
                      connectionError: true,
                      statusCode: 500,
                    });
                  }
                })
                .catch((error) => {
                  this.$interval.cancel(retryJobStatusInterval);
                  this.handleCloningError(error);
                });
            }, 3000);
          }
        }, 100);
      })
      .catch((error) => {
        this.handleCloningError(error);
      });
  }
  private handleCloningError(error: IUIError): void {
    this.statusBarService.hide();
    this.uiErrorHandlingService.handleModal(error);
  }

  private updateProgressBar(
    type: ResourceType,
    cloneJob: ICloneJob,
    cloneOptions?: IMetricCloneSelected | IGoalCloneSelected | ISelectedCloningSession,
    source?: IGoal | IMetric
  ): void {
    let opts = null;
    let statusData: IStatusData = { status: "done" };
    switch (type) {
      case ResourceType.KeyResult:
        this.metricService
          .getMetric(cloneJob.rootClonedId)
          .then((newMetric) => {
            opts = cloneOptions as IMetricCloneSelected;
            statusData = {
              status: "done_cloning_metric",
              params: {
                metric: newMetric,
                targetGoal: opts.goal,
                ...(opts.session.id !== newMetric.sessionId && { targetSession: opts.session }),
              },
            };
            this.okrEvents.broadcastGoalsChanged("metricCreated", { goalId: newMetric.goalId, metric: newMetric });
            this.$rootScope.$broadcast("goalsChanged");
            this.statusBarService.updateStatus(statusData);
          })
          .catch((error) => {
            this.handleCloningError(error);
          });
        break;
      case ResourceType.Objective:
        this.goalService
          .getGoal(cloneJob.rootClonedId)
          .then((newGoal) => {
            opts = cloneOptions as IGoalCloneSelected;
            statusData = {
              status: "done",
              action: "clone",
              params: {
                goal: source as IGoal,
                targetGoal: newGoal,
                targetSession: opts.session,
                totalItemsCounter: Object.values(cloneJob.clonedIds).filter((item) => item === "okr").length,
                includeItems: true,
              },
            };

            this.okrEvents.broadcastGoalsChanged("goalCloned", { goal: newGoal });
            this.$rootScope.$broadcast("goalsChanged");

            this.statusBarService.updateStatus(statusData);
          })
          .catch((error) => {
            this.handleCloningError(error);
          });
        break;
      case ResourceType.Session:
        opts = cloneOptions as ISelectedCloningSession;
        statusData = {
          status: "done",
          action: "clone",
          params: {
            isSessionClone: true,
            includeItems: true,
            totalItemsCounter: Object.values(cloneJob.clonedIds).filter((item) => item === "okr").length,
            targetSession: {
              id: opts.sessionId,
              title: opts.title,
            },
          },
        };
        this.statusBarService.updateStatus(statusData);
    }
  }

  private scheduleCloneJob(cloneRequest: CloneResourceRequest): IPromise<ICloneJobId> {
    const url = this.env.getApiEndpoint("/clone-jobs");
    return this.$http.post<ICloneJobId>(url, cloneRequest).then((response) => {
      return response.data;
    });
  }

  private fetchCloneJobById(target: ICloneJobId): IPromise<ICloneJob> {
    const url = this.env.getApiEndpoint("/clone-jobs/" + target.id);
    return this.$http.get<ICloneJob>(url).then((response) => {
      return response.data;
    });
  }
}
